import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { PRIORITIES, item } from "@/lib/constants";
import { fmtDate, fromNow, initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, ClipboardList, Sparkles, Loader2, CheckCircle2, ListTodo, Trash2, ArrowRight, Gavel, FileBarChart, CalendarRange, Download, Share2, Hash, Mic, Square, Video } from "lucide-react";

export default function Meetings() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", notes: "" });
  const [selected, setSelected] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [converting, setConverting] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const autoRef = useRef(false);
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  const startRec = async (auto = false) => {
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      autoRef.current = auto;
      setAutoMode(auto);
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "kayit.webm");
          const { data } = await api.post("/meetings/transcribe", fd);
          const text = data.text || "";
          setForm((f) => ({ ...f, notes: (f.notes ? f.notes.trim() + "\n" : "") + text }));
          if (autoRef.current) {
            const cur = formRef.current;
            const title = cur.title.trim() || `Sesli Toplantı Notu — ${new Date().toLocaleDateString("tr-TR")}`;
            const date = cur.date ? new Date(cur.date).toISOString() : new Date().toISOString();
            const { data: m } = await api.post("/meetings", { title, date, notes: text });
            const { data: analyzed } = await api.post(`/meetings/${m.id}/analyze`);
            qc.invalidateQueries({ queryKey: ["meetings"] });
            setCreateOpen(false); setForm({ title: "", date: "", notes: "" }); setSelected(analyzed);
            toast.success("AI özeti ve aksiyonlar hazır");
          } else {
            toast.success("Ses yazıya çevrildi");
          }
        } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        finally { setTranscribing(false); setAutoMode(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      toast.error("Mikrofona erişilemedi. Lütfen izin verin.");
    }
  };
  const stopRec = () => { try { mediaRef.current?.stop(); } catch { /* ignore */ } setRecording(false); };

  const { data = [], isLoading } = useQuery({ queryKey: ["meetings"], queryFn: async () => { const d = (await api.get("/meetings")).data; return Array.isArray(d) ? d : []; } });
  const { data: reports = [] } = useQuery({ queryKey: ["management-reports"], queryFn: async () => { const d = (await api.get("/management-reports")).data; return Array.isArray(d) ? d : []; } });
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [period, setPeriod] = useState("7");
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);

  const generateWeekly = async () => {
    setGenerating(true);
    try {
      const { data: r } = await api.post("/meetings/weekly-summary", { days: Number(period) });
      setReport(r);
      qc.invalidateQueries({ queryKey: ["management-reports"] });
      toast.success("Haftalık yönetim özeti oluşturuldu");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };
  const removeReport = async (id) => { await api.delete(`/management-reports/${id}`); toast.success("Rapor silindi"); qc.invalidateQueries({ queryKey: ["management-reports"] }); if (report?.id === id) setReport(null); };

  const { data: channels = [] } = useQuery({ queryKey: ["conversations"], queryFn: async () => (await api.get("/conversations")).data });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data });

  const shareToChannel = async (convId, name) => {
    try { await api.post(`/management-reports/${report.id}/share`, { conversation_id: convId }); toast.success(`'${name}' kanalına gönderildi`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadPdf = () => {
    if (!report) return;
    const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const bodyHtml = report.content.split("\n").map((ln) => {
      const t = ln.trim();
      if (t.startsWith("## ")) return `<h2>${esc(t.slice(3))}</h2>`;
      if (t.startsWith("# ")) return `<h1>${esc(t.slice(2))}</h1>`;
      if (t.startsWith("- ")) return `<li>${esc(t.slice(2))}</li>`;
      if (!t) return "";
      return `<p>${esc(t)}</p>`;
    }).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Açılır pencere engellendi. Lütfen izin verin."); return; }
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(report.title)}</title>
      <style>
        @page { margin: 24mm 18mm; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color:#0f172a; line-height:1.6; }
        h1.doc { font-size:22px; margin:0 0 4px; }
        .meta { color:#64748b; font-size:12px; margin-bottom:18px; border-bottom:1px solid #e2e8f0; padding-bottom:12px; }
        h2 { font-size:15px; color:#4f46e5; margin:18px 0 6px; }
        p { margin:4px 0; font-size:13px; }
        li { font-size:13px; margin:2px 0; }
        .brand { color:#4f46e5; font-weight:700; }
      </style></head><body>
      <div class="brand">WorkHub</div>
      <h1 class="doc">${esc(report.title)}</h1>
      <div class="meta">${esc(report.period_label)} • ${report.meeting_count} toplantı • ${report.action_count} aksiyon • ${fmtDate(report.created_at)}</div>
      ${bodyHtml}
      </body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 350);
  };

  const create = async () => {
    if (!form.title.trim()) { toast.error("Başlık gerekli"); return; }
    try {
      const { data: m } = await api.post("/meetings", { title: form.title, date: form.date ? new Date(form.date).toISOString() : null, notes: form.notes });
      toast.success("Toplantı raporu oluşturuldu");
      qc.invalidateQueries({ queryKey: ["meetings"] });
      setCreateOpen(false); setForm({ title: "", date: "", notes: "" });
      setSelected(m);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openMeeting = async (id) => { const { data: m } = await api.get(`/meetings/${id}`); setSelected(m); };
  const remove = async (id) => { await api.delete(`/meetings/${id}`); toast.success("Rapor silindi"); qc.invalidateQueries({ queryKey: ["meetings"] }); if (selected?.id === id) setSelected(null); };

  const analyze = async () => {
    setAnalyzing(true);
    try { const { data: m } = await api.post(`/meetings/${selected.id}/analyze`); setSelected(m); qc.invalidateQueries({ queryKey: ["meetings"] }); toast.success("AI özeti ve aksiyonlar hazır"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const convert = async (ai) => {
    setConverting(ai.id);
    try { const { data } = await api.post(`/meetings/${selected.id}/convert`, { item_id: ai.id }); toast.success("Görev oluşturuldu"); await openMeeting(selected.id); qc.invalidateQueries({ queryKey: ["tasks"] }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConverting(null); }
  };

  const [convertTarget, setConvertTarget] = useState(null);
  const [convForm, setConvForm] = useState({ assignees: [], due: "" });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestReason, setSuggestReason] = useState("");
  const openConvert = (a) => { setConvForm({ assignees: [], due: "" }); setSuggestReason(""); setConvertTarget(a); };
  const suggestAssignees = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/meetings/${selected.id}/suggest-assignee`, { item_id: convertTarget.id });
      setConvForm((f) => ({ ...f, assignees: data.suggested }));
      setSuggestReason(data.reason || "");
      toast.success("AI önerisi uygulandı");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };
  const doConvert = async () => {
    const a = convertTarget;
    setConverting(a.id);
    try {
      await api.post(`/meetings/${selected.id}/convert`, {
        item_id: a.id,
        assignees: convForm.assignees,
        due_date: convForm.due ? new Date(convForm.due).toISOString() : null,
      });
      toast.success("Görev oluşturuldu");
      setConvertTarget(null);
      await openMeeting(selected.id);
      qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConverting(null); }
  };

  return (
    <div>
      <PageHeader title="Toplantı Raporları" subtitle="Toplantı notlarını AI ile özetleyin ve göreve dönüştürün"
        actions={<div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setReport(null); setWeeklyOpen(true); }} data-testid="weekly-summary-button"><FileBarChart className="h-4 w-4 mr-1.5" /> Haftalık Özet</Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="new-meeting-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Toplantı</Button>
        </div>} />

      {isLoading ? <ListSkeleton /> : data.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Henüz toplantı raporu yok" description="Bir toplantı notu ekleyin, AI sizin için özetlesin ve aksiyon maddelerini çıkarsın." action={<Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Yeni Toplantı</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((m) => {
            const converted = (m.action_items || []).filter((a) => a.task_id).length;
            return (
              <Card key={m.id} className="p-5 group cursor-pointer hover:shadow-md transition-all" onClick={() => openMeeting(m.id)} data-testid={`meeting-${m.id}`}>
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-600 grid place-items-center"><ClipboardList className="h-5 w-5" /></div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button onClick={(e) => e.stopPropagation()} className="md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader><AlertDialogTitle>Raporu sil</AlertDialogTitle><AlertDialogDescription>Bu toplantı raporu kalıcı olarak silinsin mi?</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={() => remove(m.id)} data-testid="confirm-delete-button">Sil</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <h3 className="font-semibold mt-3 truncate">{m.title}</h3>
                <p className="text-xs text-muted-foreground">{fmtDate(m.date)}</p>
                <div className="flex items-center gap-2 mt-3">
                  {m.analyzed ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">AI özeti hazır</Badge> : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Analiz bekliyor</Badge>}
                  {m.action_items?.length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><ListTodo className="h-3 w-3" /> {converted}/{m.action_items.length} göreve dönüştü</span>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {reports.length > 0 && (
        <Card className="mt-6 p-5" data-testid="reports-list">
          <div className="flex items-center gap-2 mb-3"><FileBarChart className="h-4 w-4 text-indigo-600" /><h3 className="font-semibold">Geçmiş Yönetim Özetleri</h3></div>
          <div className="divide-y">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2.5 group">
                <button onClick={() => { setReport(r); setWeeklyOpen(true); }} className="flex-1 text-left" data-testid={`report-${r.id}`}>
                  <p className="text-sm font-medium">{r.title}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(r.created_at)} • {r.meeting_count} toplantı • {r.action_count} aksiyon</p>
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><button className="md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Raporu sil</AlertDialogTitle><AlertDialogDescription>Bu yönetim özeti kalıcı olarak silinsin mi?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={() => removeReport(r.id)} data-testid="confirm-delete-button">Sil</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="meeting-dialog">
          <DialogHeader><DialogTitle>Yeni Toplantı Raporu</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn. Haftalık ekip toplantısı" className="mt-1.5" data-testid="meeting-title-input" /></div>
            <div><Label>Tarih</Label><Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Toplantı Notları</Label>
                {!recording ? (
                  <div className="flex items-center gap-1.5">
                    <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => startRec(false)} disabled={transcribing} data-testid="voice-record-start">
                      {transcribing && !autoMode ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mic className="h-3.5 w-3.5 mr-1" />} Sesli Not
                    </Button>
                    <Button type="button" size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700" onClick={() => startRec(true)} disabled={transcribing} data-testid="voice-auto-summarize">
                      {transcribing && autoMode ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} {transcribing && autoMode ? "AI işliyor..." : "Konuş & Özetle"}
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="destructive" size="sm" className="h-7 animate-pulse" onClick={stopRec} data-testid="voice-record-stop"><Square className="h-3.5 w-3.5 mr-1" /> {autoMode ? "Durdur & Özetle" : "Durdur & Çevir"}</Button>
                )}
              </div>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={7} placeholder="Toplantıda konuşulanları buraya yapıştırın, yazın veya 'Sesli Not' ile konuşarak ekleyin. AI bunları özetleyip aksiyon maddelerine çevirecek." className="mt-0" data-testid="meeting-notes-input" />
              {recording && <p className="text-xs text-rose-500 mt-1 flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" /> Kayıt sürüyor... {autoMode ? "Bitince \"Durdur & Özetle\"ye basın; AI otomatik özetler." : "Bitince \"Durdur & Çevir\"e basın."}</p>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>İptal</Button><Button onClick={create} data-testid="meeting-save-button">Oluştur</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="meeting-detail-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.title}</DialogTitle></DialogHeader>
              <div className="flex items-center justify-between gap-2 -mt-2">
                <p className="text-xs text-muted-foreground">{fmtDate(selected.date)} • {selected.created_by_name}</p>
                <Button size="sm" variant="outline" className="text-primary" onClick={() => nav(`/video?room=workhub-mtg-${selected.id}`)} data-testid="meeting-join-video">
                  <Video className="h-4 w-4 mr-1.5" /> Görüntülü Katıl
                </Button>
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-950/30 dark:border-indigo-900 p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-300"><Sparkles className="h-4 w-4" /> {selected.analyzed ? "Yeniden analiz edebilirsiniz" : "Notları AI ile özetleyin"}</div>
                <Button size="sm" onClick={analyze} disabled={analyzing} data-testid="meeting-analyze-button">
                  {analyzing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} {selected.analyzed ? "Yeniden Analiz Et" : "AI ile Analiz Et"}
                </Button>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Toplantı Notları</Label>
                <div className="mt-1.5 text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-3 max-h-40 overflow-y-auto">{selected.notes || "Not yok."}</div>
              </div>

              {selected.analyzed && (
                <>
                  <div>
                    <h4 className="font-semibold flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-indigo-600" /> Yönetici Özeti</h4>
                    <div className="text-sm whitespace-pre-wrap bg-card border rounded-lg p-3">{selected.summary || "-"}</div>
                  </div>

                  {selected.decisions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2"><Gavel className="h-4 w-4 text-slate-600" /> Alınan Kararlar</h4>
                      <ul className="space-y-1">
                        {selected.decisions.map((d, i) => <li key={i} className="text-sm flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {d}</li>)}
                      </ul>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold flex items-center gap-2 mb-2"><ListTodo className="h-4 w-4 text-indigo-600" /> Aksiyon Maddeleri</h4>
                    <div className="space-y-2">
                      {selected.action_items?.length ? selected.action_items.map((a) => {
                        const pr = item(PRIORITIES, a.priority);
                        return (
                          <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`action-item-${a.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{a.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className={`text-[10px] ${pr.chip}`}>{pr.label}</Badge>
                                {a.due_hint && <span className="text-[10px] text-muted-foreground">⏱ {a.due_hint}</span>}
                              </div>
                            </div>
                            {a.task_id ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" /> Göreve dönüştürüldü</Badge>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => openConvert(a)} disabled={converting === a.id} data-testid={`convert-item-${a.id}`}>
                                {converting === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ArrowRight className="h-3.5 w-3.5 mr-1" /> Göreve Dönüştür</>}
                              </Button>
                            )}
                          </div>
                        );
                      }) : <p className="text-sm text-muted-foreground">Aksiyon maddesi bulunamadı.</p>}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert action item -> task dialog */}
      <Dialog open={!!convertTarget} onOpenChange={(v) => !v && setConvertTarget(null)}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader><DialogTitle>Göreve Dönüştür</DialogTitle></DialogHeader>
          {convertTarget && (
            <div className="space-y-4">
              <div className="text-sm p-3 rounded-lg bg-muted/50 border">{convertTarget.title}</div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Sorumlu Kişiler {convForm.assignees.length > 0 && <span className="text-muted-foreground">({convForm.assignees.length})</span>}</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-indigo-600" onClick={suggestAssignees} disabled={suggesting} data-testid="convert-ai-suggest">
                    {suggesting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI Öner
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {members.map((m) => {
                    const on = convForm.assignees.includes(m.id);
                    return (
                      <button key={m.id} type="button" data-testid={`convert-assignee-${m.id}`}
                        onClick={() => setConvForm({ ...convForm, assignees: on ? convForm.assignees.filter((x) => x !== m.id) : [...convForm.assignees, m.id] })}
                        className={`flex items-center gap-1.5 rounded-full pl-1 pr-2.5 py-1 text-xs border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-muted hover:bg-accent"}`}>
                        <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initials(m.name)}</AvatarFallback></Avatar>{m.name}
                      </button>
                    );
                  })}
                </div>
                {suggestReason && (
                  <div className="mt-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 rounded-lg p-2 flex gap-1.5" data-testid="convert-suggest-reason">
                    <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" /> <span>{suggestReason}</span>
                  </div>
                )}
              </div>
              <div>
                <Label>Son Tarih</Label>
                <Input type="date" value={convForm.due} onChange={(e) => setConvForm({ ...convForm, due: e.target.value })} className="mt-1.5" data-testid="convert-due-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertTarget(null)}>İptal</Button>
            <Button onClick={doConvert} disabled={!!converting} data-testid="convert-confirm-button">{converting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Görev Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weekly management summary dialog */}
      <Dialog open={weeklyOpen} onOpenChange={setWeeklyOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="weekly-summary-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileBarChart className="h-5 w-5 text-indigo-600" /> Haftalık Yönetim Özeti</DialogTitle></DialogHeader>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Dönem</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="mt-1.5" data-testid="weekly-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Son 7 gün</SelectItem>
                  <SelectItem value="14">Son 14 gün</SelectItem>
                  <SelectItem value="30">Son 30 gün</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateWeekly} disabled={generating} data-testid="weekly-generate-button">
              {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Oluştur
            </Button>
          </div>

          {generating && <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Toplantılar AI ile birleştiriliyor...</div>}

          {report && !generating && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                <CalendarRange className="h-3.5 w-3.5" /> {report.period_label} • {report.meeting_count} toplantı • {report.action_count} aksiyon • {fmtDate(report.created_at)}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="report-download-pdf"><Download className="h-3.5 w-3.5 mr-1.5" /> PDF İndir</Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="report-share-button"><Share2 className="h-3.5 w-3.5 mr-1.5" /> Kanala Gönder</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Sohbet kanalı seçin</DropdownMenuLabel>
                    {channels.length ? channels.map((c) => (
                      <DropdownMenuItem key={c.id} onClick={() => shareToChannel(c.id, c.name)} data-testid={`share-channel-${c.id}`}>
                        <Hash className="h-4 w-4 mr-2" /> {c.name}
                      </DropdownMenuItem>
                    )) : <DropdownMenuItem disabled>Kanal yok</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="text-sm whitespace-pre-wrap bg-muted/40 border rounded-lg p-4 leading-relaxed" data-testid="weekly-report-content">{report.content}</div>
            </div>
          )}

          {!report && !generating && (
            <p className="text-sm text-muted-foreground py-6 text-center">Bir dönem seçin ve "Oluştur"a tıklayın. AI, o dönemdeki tüm toplantı özetlerini, kararları ve aksiyonları tek bir yönetici raporunda birleştirir.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
